/**
 * Query keys do domínio financeiro (Painel Financeiro do CRM).
 *
 * P3-2 (auditoria 2026-07 / #64,#65,#66,#70,#114): as queries do painel
 * (`student_invoices`, `student_monthly_billing`, `club_expenses`) usavam keys
 * inline (`['financial-invoices', ...]`) que NENHUMA mutação invalidava — os
 * mutations invalidavam `['student-invoices']` / `qk.students.all`, prefixos
 * distintos. Resultado: dar baixa numa fatura/billing ou lançar despesa não
 * atualizava os cards nem o gráfico até um refetch manual.
 *
 * Fonte-de-verdade única (IR-RQ-001): o painel usa estas keys e as mutações
 * financeiras invalidam `qk.financial.all` (prefix-match cobre as três).
 *
 * `viewScope` participa da key porque os valores mudam entre 'club' e 'network'.
 */
import { tuple } from "./core";

const root = ["financial"] as const;

export const financial = {
  /** Prefixo raiz — mutações financeiras invalidam isto (cobre tudo abaixo). */
  all: root,
  /** Faturas avulsas (`student_invoices`) do painel financeiro. */
  invoices: (clubId: string | null | undefined, viewScope: unknown) =>
    tuple(root, "invoices", clubId, viewScope),
  /** Mensalidade consolidada (`student_monthly_billing`) do painel. */
  billings: (clubId: string | null | undefined, viewScope: unknown) =>
    tuple(root, "billings", clubId, viewScope),
  /** Despesas (`club_expenses`) do painel financeiro. */
  expenses: (clubId: string | null | undefined, viewScope: unknown) =>
    tuple(root, "expenses", clubId, viewScope),
  /** Vendas do PDV (`club_sales` fechadas) do painel financeiro (C2). */
  sales: (clubId: string | null | undefined, viewScope: unknown) =>
    tuple(root, "sales", clubId, viewScope),
  /**
   * Central "Controle de cobranças" (aba de /financeiro): resumo da RPC
   * `cobranca_controle_resumo` e as contagens complementares. Sob `financial`:
   * baixa e "marcar como enviado" invalidam `qk.financial.all`.
   */
  billingControl: (clubIds: readonly string[] | null | undefined, competencia: string | null, extra?: unknown) =>
    tuple(root, "billing-control", clubIds, competencia, extra),
  /**
   * Catálogo de preços dos planos do SaaS (`club_plan_prices`, ativos) — o que
   * o diálogo "Alterar plano" do CRM mostra. Só leitura: a troca de plano é do
   * admin da PPS desde 11/10/2026.
   */
  planPrices: tuple(root, "plan-prices"),
  /**
   * Fluxo de caixa (aba de /financeiro): RPCs `fluxo_de_caixa_lista`,
   * `_totais` e `_responsaveis`. Sob `financial`: fechar comanda, pagar despesa
   * e dar baixa já invalidam `qk.financial.all`.
   */
  cashFlow: (clubIds: readonly string[] | null | undefined, parte: "lista" | "totais" | "responsaveis" | "omitidas", filtros: unknown) =>
    tuple(root, "cash-flow", clubIds, parte, filtros),
  /**
   * Recebimentos por professor (aba de /financeiro): RPCs
   * `recebimentos_por_professor_lista`, `_totais` e `_opcoes`. Sob
   * `financial`: dar baixa numa fatura já invalida `qk.financial.all`.
   */
  byTeacher: (clubIds: readonly string[] | null | undefined, parte: "lista" | "totais" | "opcoes", filtros: unknown) =>
    tuple(root, "by-teacher", clubIds, parte, filtros),
} as const;
